import React from 'react';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useFormik } from 'formik';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { SendMessageInput } from 'components/Inputs/SendMessageInput';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { ConversationCopyButton } from './ConversationCopyButton';
import { WarningListAlert } from 'common/ui';
import { ConversationActionState } from 'modules/conversations/hooks/useConversationActionState';
import { ConversationColleagueTyping } from './ConversationColleagueTyping';
import { Channels } from 'generated-graphql/types';

const restartInfoContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const buttonsGroupContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: '15px'
};

const inChargeLine: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
    color: 'customGrey.dark'
  }
};

const inChargeText: SxProps = {
  fontSize: '14px',
  lineHeight: '20px',
  color: 'customGrey.dark'
};

const agentActions: SxProps = {
  ...buttonsGroupContainer,
  pt: '20px'
};

const takeoverButton: SxProps = {
  px: '20px',
  py: '14px'
};

const caption: SxProps = {
  mt: '15px',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '18px',
  color: 'customGrey.dark'
};

type Props = {
  channel: Channels | null;
  conversationId: string;
  isLoading: boolean;
  state: ConversationActionState;
  formik: ReturnType<typeof useFormik<{ messageBody: string }>>;
  onCopyConversation: () => void;
  onRestartConversation: () => void;
  onUpdateConversation: (type: 'user' | 'agent') => void;
};

export const ConversationActions: React.FC<Props> = ({
  channel,
  conversationId,
  isLoading,
  state,
  formik,
  onCopyConversation: handleCopyConversation,
  onRestartConversation: handleRestartConversation,
  onUpdateConversation: handleUpdateConversation
}) => {
  const { track } = useTracking();

  if (state === ConversationActionState.HIDDEN) {
    return <Box />;
  } else if (state === ConversationActionState.WHATSAPP_RESTARTING) {
    return (
      <Box sx={restartInfoContainer}>
        <Typography display="flex" variant="body1">
          The conversation will continue once the candidate replies.
        </Typography>
        <ConversationCopyButton onClick={handleCopyConversation} />
      </Box>
    );
  } else if (state === ConversationActionState.DIFFERENT_CONVERSATION) {
    return (
      <Box>
        <WarningListAlert
          title="Candidate in another conversation"
          bulletPoints={[
            "Candidates can't have simultaneous conversations",
            'To takeover this conversation, wait for the other conversation to close'
          ]}
        />
        <Box sx={buttonsGroupContainer}>
          <ConversationCopyButton onClick={handleCopyConversation} />
        </Box>
      </Box>
    );
  } else if (state === ConversationActionState.WHATSAPP_WAITING_FOR_REPLY) {
    return (
      <Box>
        <WarningListAlert
          title="Waiting for candidate reply"
          bulletPoints={[
            "Popp will nudge them after 24 hours if there's no response",
            'Once the candidate replies, the conversation opens with Popp in control'
          ]}
        />
        <Box sx={buttonsGroupContainer}>
          <ConversationCopyButton onClick={handleCopyConversation} />
        </Box>
      </Box>
    );
  } else if (state === ConversationActionState.WHATSAPP_CAN_RESTART) {
    return (
      <Box>
        <WarningListAlert
          title="WhatsApp has closed this conversation due to inactivity."
          bulletPoints={[
            'To continue it, re-open the conversation',
            "Popp messages the candidate, nudging them after 24 hours if there's no response",
            'Once the candidate replies, the conversation re-opens with Popp in control'
          ]}
        />
        <Box sx={buttonsGroupContainer}>
          <Button
            variant="primary"
            onClick={() => {
              track(TrackingEventName.CampaignDashboardConversationRestart);
              handleRestartConversation();
            }}
            disabled={isLoading}
          >
            Re-open conversation
          </Button>
          <ConversationCopyButton onClick={handleCopyConversation} />
        </Box>
      </Box>
    );
  } else if (state === ConversationActionState.HUMAN_TAKEOVER) {
    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You are in charge of the conversation.
        </Typography>
        <Stack gap="10px">
          <Typography variant="field">Type your reply</Typography>
          <ConversationColleagueTyping conversationId={conversationId} />
          <Box>
            <SendMessageInput
              conversationId={conversationId}
              onSend={formik.submitForm}
              value={formik.values.messageBody}
              onChange={formik.handleChange}
              id="messageBody"
              name="messageBody"
              disabled={isLoading || formik.isSubmitting}
            />
            <Box sx={buttonsGroupContainer} mt={2}>
              <Button
                variant="secondary"
                onClick={() => {
                  track(
                    TrackingEventName.CampaignDashboardConversationHandover
                  );
                  handleUpdateConversation('agent');
                }}
                disabled={isLoading}
              >
                Handover conversation
              </Button>
              <ConversationCopyButton onClick={handleCopyConversation} />
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  } else if (
    state === ConversationActionState.HUMAN_TAKEOVER_WITH_NO_HANDOVER
  ) {
    return (
      <Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          You are in charge of the conversation.
        </Typography>
        <Stack gap="10px">
          <Typography variant="field">Type your reply</Typography>
          <ConversationColleagueTyping conversationId={conversationId} />
          <Box>
            <SendMessageInput
              conversationId={conversationId}
              onSend={formik.submitForm}
              value={formik.values.messageBody}
              onChange={formik.handleChange}
              id="messageBody"
              name="messageBody"
              disabled={isLoading || formik.isSubmitting}
            />
            <Box sx={buttonsGroupContainer} mt={2}>
              <ConversationCopyButton onClick={handleCopyConversation} />
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  } else {
    return (
      <Box>
        <Box sx={inChargeLine}>
          <AutoAwesomeOutlinedIcon />
          <Typography sx={inChargeText}>
            Popp is in charge of the conversation
          </Typography>
        </Box>
        <Box sx={agentActions}>
          <Button
            variant="secondary"
            sx={takeoverButton}
            onClick={() => {
              track(TrackingEventName.CampaignDashboardConversationTakeover);
              handleUpdateConversation('user');
            }}
            disabled={
              isLoading ||
              state === ConversationActionState.AGENT_CONTROL_DISABLED
            }
          >
            Takeover conversation
          </Button>

          <ConversationCopyButton onClick={handleCopyConversation} />
        </Box>

        {channel && channel !== Channels.Email && (
          <Typography sx={caption}>
            You can pause and take over a conversation as long as the last
            activity was less than 24 hours ago.
          </Typography>
        )}
      </Box>
    );
  }
};
